import { useMemo } from 'react';

import {
	getActionInvocationStatus,
	isStoreAssistant,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';

type ActionInvocationStatus = ReturnType<typeof getActionInvocationStatus>;

/**
 * Live invocation statuses for one or more plan steps, keyed by invocation id,
 * read straight from the chat store.
 *
 * `getStatus` is normally threaded through as a prop, but it goes stale inside
 * the canvas/other stores — reading the conversation store directly avoids that.
 * Taking a list (rather than a single id) lets a caller classify a whole plan in
 * one hook call, which a per-id hook could not do without breaking the rules of
 * hooks. Pass a memoised `invocationIds` array so the result stays referentially
 * stable across renders.
 */
export function useActionInvocationStatuses(
	conversationId: string | undefined,
	invocationIds: string[],
): Record<string, ActionInvocationStatus> {
	const [messages] = useChatMessages({ conversationId: conversationId ?? '' });

	return useMemo(() => {
		const statuses: Record<string, ActionInvocationStatus> = {};
		for (const invocationId of invocationIds) {
			let resolved: ActionInvocationStatus = 'idle';
			for (const message of messages) {
				if (isStoreAssistant(message)) {
					const status = getActionInvocationStatus(invocationId, message.invokedActions);
					if (status !== 'idle') {
						resolved = status;
						break;
					}
				}
			}
			statuses[invocationId] = resolved;
		}
		return statuses;
	}, [messages, invocationIds]);
}
